import {Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {RouterLink, RouterLinkActive, RouterOutlet} from "@angular/router";
import {DateRangeSelectorComponent} from '../../../layout/date-range-selector/date-range-selector.component';
import { DatePipe } from '@angular/common';
import {FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {DateRange} from '../../../../types/date-range';
import {Offer} from '../../../../models/offer';
import {ApiClientService} from '../../../../services/api-client/api-client.service';
import {ExportProgress, StatisticService} from '../../../../services/statistic/statistic.service';
import {LoaderService} from '../../../../services/loader/loader.service';
import {DropdownSelectComponent} from '../../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../../layout/dropdown-select-option/dropdown-select-option.component';
import {FilterStorageService} from '../../../../services/filter-storage/filter-storage.service';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-report-tab',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    DateRangeSelectorComponent,
    ReactiveFormsModule,
    DropdownSelectComponent,
    DropdownSelectOptionComponent
],
  providers: [DatePipe],
  templateUrl: './report-tab.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './report-tab.component.scss'
})
export class ReportTabComponent implements OnInit, OnDestroy {

  offers: Offer[] = [];
  dateRangeInvalid: boolean = false;
  savedDate: any = null;
  exportProgress: ExportProgress | null = null;
  filters: FormGroup = new FormGroup({
    startDate: new FormControl(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)),
    endDate: new FormControl(new Date(new Date().getFullYear(), new Date().getMonth(), 0)),
    offerIds: new FormControl([]),
    subid: new FormControl(''),
    tc1: new FormControl(''),
    tc2: new FormControl(''),
    tc3: new FormControl(''),
    tc4: new FormControl(''),
  });

  private exportProgressSubscription: Subscription | null = null;

  @ViewChild(DateRangeSelectorComponent) dateRangeSelector!: DateRangeSelectorComponent;

  constructor(
    protected loader: LoaderService,
    private statisticService: StatisticService,
    private apiClient: ApiClientService,
    private datePipe: DatePipe,
    private filterStorage: FilterStorageService,
  ) {
  }

  exportCsv(): void {
    if (this.exportProgress !== null) {
      return;
    }

    this.statisticService.$exportCsv.next();
  }

  ngOnInit(): void {
    this.exportProgressSubscription = this.statisticService.$exportProgress
      .subscribe((progress: ExportProgress | null) => this.exportProgress = progress);

    const savedFilters = this.filterStorage.load('reports');
    if (savedFilters) {
      this.filters.patchValue({
        offerIds: savedFilters.offerIds ?? [],
        subid: savedFilters.subid ?? '',
        tc1: savedFilters.tc1 ?? '',
        tc2: savedFilters.tc2 ?? '',
        tc3: savedFilters.tc3 ?? '',
        tc4: savedFilters.tc4 ?? '',
      });
      this.savedDate = savedFilters.date ?? null;
    }

    this.fetchActiveOffers(() => this.onSubmit());
  }

  ngOnDestroy(): void {
    this.exportProgressSubscription?.unsubscribe();
  }

  onSubmit(): void {
    this.saveFilters();

    const filters: any = this.filters.value;
    filters.startDate = this.datePipe.transform(this.filters.value.startDate, 'yyyy-MM-dd');
    filters.endDate = this.datePipe.transform(this.filters.value.endDate, 'yyyy-MM-dd');

    for (const [key, value] of Object.entries(filters)) {
      if (value === '' || (Array.isArray(value) && !value.length)) {
        delete filters[key];
      }
    }

    this.statisticService.$filters.next(filters);
  }

  updateRange(dateRange: DateRange): void {
    this.filters.get('startDate')?.patchValue(dateRange.start);
    this.filters.get('endDate')?.patchValue(dateRange.end);

    this.fetchActiveOffers();
  }

  private saveFilters(): void {
    this.filterStorage.save('reports', {
      offerIds: this.filters.value.offerIds,
      subid: this.filters.value.subid,
      tc1: this.filters.value.tc1,
      tc2: this.filters.value.tc2,
      tc3: this.filters.value.tc3,
      tc4: this.filters.value.tc4,
      date: this.dateRangeSelector ? {
        selector: this.dateRangeSelector.dateSelector,
        from: this.dateRangeSelector.dateFromSelector,
        to: this.dateRangeSelector.dateToSelector,
      } : this.savedDate,
    });
  }

  private fetchActiveOffers(callback?: () => void): void {
    this.statisticService.fetchActiveOffers({
      startDate: this.datePipe.transform(this.filters.value.startDate, 'yyyy-MM-dd'),
      endDate: this.datePipe.transform(this.filters.value.endDate, 'yyyy-MM-dd'),
    }).subscribe((offers: Offer[]) => {
      this.offers = offers;

      // Drop selected offers that are not active on the new period
      const offerIds = (this.filters.value.offerIds ?? [])
        .filter((id: any) => offers.some((offer: Offer) => offer.id == id));
      this.filters.get('offerIds')?.patchValue(offerIds);

      if (callback) {
        callback();
      }
    });
  }
}
